import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { AskEvaClient, AskEvaSuggestionItem, AskEvaSuggestionsResponse } from 'api-clients/ai-api-client';
import { filter, map } from 'rxjs';
import { EvaSidebarService } from 'src/app/eva-sidebar/eva-sidebar.service';
import { EvaPagePromptContext, EvaPagePromptRule, pagePromptRules } from 'src/app/eva-sidebar/eva-suggested-prompts/eva-page-prompt-rules';
import { EvaQuestionAnswerService } from 'src/app/eva-sidebar/shared/services/eva-question-answer.service';
import { EvaSharedApisService } from 'src/app/eva-sidebar/shared/services/eva-shared-apis.service';
import { globalWindow } from 'src/shared/window-globals';

@Injectable()
export class EvaSuggestedPromptsService {

  readonly #evaQuestionAnswerService = inject(EvaQuestionAnswerService);
  readonly #evaSidebarService = inject(EvaSidebarService);
  readonly #askEvaClient = inject(AskEvaClient);
  readonly #evaSharedApisService = inject(EvaSharedApisService);

  // `optional: true` is defensive, not load-bearing: Router is providedIn: 'root', so the legacy
  // shell's injector constructs one even though shell.module.ts registers no RouterModule. The
  // fallback branch below is therefore unreachable today. What DOES differ is that the shell never
  // calls initialNavigation(), so router.events never emits and #currentPath correctly stays at its
  // initialValue — which is exactly right for EZ4, where every navigation is a full document load.
  readonly #router = inject(Router, { optional: true });

  /**
   * The current page path, in one URL space for both hosts.
   *
   * The Router is a change signal, not the source of truth: it exists only because SPA navigation
   * changes the URL without a document load, so a single read would go stale. EZ4 has the opposite
   * property — every navigation is a full document load — so reading once there is correct.
   *
   * initialValue matters: <ez-eva-sidebar /> lives in app.component.html, so this service can be
   * constructed after the first NavigationEnd. Listening to events alone would miss a hard reload
   * landing directly on a matching page.
   */
  readonly #currentPath = this.#router
    ? toSignal(
      this.#router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => globalWindow.location.pathname),
      ),
      { initialValue: globalWindow.location.pathname },
    )
    : signal(globalWindow.location.pathname).asReadonly();

  readonly #hasQuestions = computed(() =>
    !!this.#evaQuestionAnswerService.questionAnswers()?.questionAndAnswers?.length
  );

  #abortController?: AbortController;
  readonly #suggestions = signal<AskEvaSuggestionItem[]>([]);

  /**
   * Suggestions fetched via ProcessSuggestions for the most recent turn. Populated only by
   * fetchSuggestions() — never derived from ProcessQuery's response.
   */
  readonly #aiPrompts = computed<string[]>(() =>
    this.#suggestions().map(suggestion => suggestion.text?.trim()).filter((text): text is string => !!text)
  );

  readonly #activeRule = computed<EvaPagePromptRule | null>(() =>
    pagePromptRules.find(rule => rule.match.test(this.#currentPath())) ?? null
  );

  /**
   * Active policy count for the applicant in #policyCountApplicantId. Stays undefined until a rule
   * that needs it resolves one, and after a failed lookup — so a rule that reads it can tell
   * "not known" from "none", and hide a policy prompt rather than guess.
   */
  readonly #activePolicyCount = signal<number | undefined>(undefined);
  #policyCountApplicantId: number | null = null;

  /** The applicant a policy count is wanted for: set only while a rule that needs one is active. */
  readonly #policyCountTarget = computed<number | null>(() =>
    this.#activeRule()?.needsPolicyCount
      ? this.#evaSidebarService.applicantContext()?.ApplicantID ?? null
      : null
  );

  /** LOB for #policyPreviewTarget. Undefined until resolved and after a failed lookup, so a rule can
   * tell "not known" from "not auto" and withhold its prompt rather than guess. */
  readonly #activePolicyLob = signal<string | undefined>(undefined);

  /** Renewal flag for #policyPreviewTarget. Undefined carries the same "not known" meaning, so a rule
   * withholds the compare prompt rather than offering a comparison with nothing on the other side. */
  readonly #hasPendingRenewal = signal<boolean | undefined>(undefined);

  /** The policy the two flags above describe, re-checked after every await. */
  #policyPreviewPolicyId: number | null = null;

  /**
   * Set only while a rule needing either policy flag is active. Read from the rule's own match, so
   * the URL is parsed once, by the regex that selected the rule.
   *
   * Both flags come from one preview response, so a rule wanting both resolves them in a single
   * request rather than one per flag.
   */
  readonly #policyPreviewTarget = computed<number | null>(() => {
    const rule = this.#activeRule();
    if (!rule?.needsPolicyLob && !rule?.needsPendingRenewal) {
      return null;
    }
    const policyMasterId = +(rule.match.exec(this.#currentPath())?.[1] ?? 0);
    return policyMasterId ? policyMasterId : null;
  });

  /** The one context both build() and excludedStaticPrompts() read, so they cannot disagree. */
  readonly #promptContext = computed<EvaPagePromptContext>(() => ({
    applicantName: this.#evaSidebarService.applicantContext()?.ApplicantName,
    activePolicyCount: this.#activePolicyCount(),
    activePolicyLob: this.#activePolicyLob(),
    hasPendingRenewal: this.#hasPendingRenewal(),
  }));

  /** Static prompts the active page rule wants hidden, e.g. ones its own prompts duplicate. */
  readonly excludedStaticPrompts = computed<readonly string[]>(() =>
    this.#activeRule()?.excludedStaticPrompts?.(this.#promptContext()) ?? []
  );

  /** Prompts contributed by the current page, before AI prompts take precedence. */
  readonly #pagePrompts = computed<string[]>(() => {
    const rule = this.#activeRule();
    if (!rule || rule.newChatOnly && this.#hasQuestions()) {
      return [];
    }
    return rule.build(this.#promptContext());
  });

  /** Any AI-supplied set replaces the page's own prompts. */
  readonly #prompts = computed<string[]>(() => {
    const aiPrompts = this.#aiPrompts();
    return aiPrompts.length ? aiPrompts : this.#pagePrompts();
  });

  /**
   * What the UI should actually render. Empty while a request is in flight, so a stale set cannot
   * be tapped, and while a HITL confirmation is pending, where the chat input is already disabled.
   */
  readonly visiblePrompts = computed<string[]>(() => {
    if (this.#evaQuestionAnswerService.isCallingApi() || this.#evaQuestionAnswerService.hasPendingConfirmation()) {
      return [];
    }
    return this.#prompts();
  });

  constructor() {
    // Resolves the count as the agent navigates between applicants. Reads through EvaSharedApisService's
    // cache, so returning to an applicant already seen in this sidebar costs no request.
    effect(() => {
      const applicantId = this.#policyCountTarget();
      if (applicantId === this.#policyCountApplicantId) {
        return;
      }
      this.#policyCountApplicantId = applicantId;
      this.#activePolicyCount.set(undefined);
      if (applicantId !== null) {
        void this.#loadActivePolicyCount(applicantId);
      }
    });

    // #policyPreviewTarget is a computed, so this only re-runs when the target policy actually
    // changes. On EZ4 that needs a full document load, so it settles once.
    effect(() => {
      const policyMasterId = this.#policyPreviewTarget();
      if (policyMasterId === this.#policyPreviewPolicyId) {
        return;
      }
      this.#policyPreviewPolicyId = policyMasterId;
      this.#clearPolicyFlags();
      if (policyMasterId !== null) {
        void this.#loadActivePolicyPreview(policyMasterId);
      }
    });
  }

  /** Returns both policy flags to "not known", which is what withholds their prompts. */
  #clearPolicyFlags(): void {
    this.#activePolicyLob.set(undefined);
    this.#hasPendingRenewal.set(undefined);
  }

  /**
   * Resolves both policy-derived flags from a single preview read. Reads through
   * EvaSharedApisService's cache, so a policy already seen in this sidebar — including one whose
   * source link was hovered — costs no request.
   *
   * Failures are deliberately quiet: this only decides whether extra suggestions appear, and leaving
   * the flags undefined hides them. A deleted policy resolves to null so the needed signals will also get set to undefined.
   */
  async #loadActivePolicyPreview(policyMasterId: number): Promise<void> {
    try {
      const preview = await this.#evaSharedApisService.getPolicyPreview(policyMasterId);
      if (this.#policyPreviewPolicyId === policyMasterId) {
        this.#activePolicyLob.set(preview?.lob);
        this.#hasPendingRenewal.set(preview?.hasPendingRenewal);
      }
    } catch {
      if (this.#policyPreviewPolicyId === policyMasterId) {
        this.#clearPolicyFlags();
      }
    }
  }

  /**
   * Resolves the applicant's active policy count. Failures are deliberately quiet: this only decides
   * whether an extra suggestion appears, and leaving the count undefined hides it — a suggestion that
   * cannot be offered is not worth a banner.
   *
   * Both branches re-check the applicant after the await, because the agent can navigate away
   * mid-request: an unguarded write would let a late result for the previous applicant land on top of
   * the current one's.
   */
  async #loadActivePolicyCount(applicantId: number): Promise<void> {
    try {
      const applicant = await this.#evaSharedApisService.getApplicant(applicantId);
      if (this.#policyCountApplicantId === applicantId) {
        this.#activePolicyCount.set(applicant?.activePolicyCount);
      }
    } catch {
      if (this.#policyCountApplicantId === applicantId) {
        this.#activePolicyCount.set(undefined);
      }
    }
  }

  /** Cancels any in-flight suggestions fetch and clears whatever is currently shown. */
  abort(): void {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#suggestions.set([]);
  }

  /**
   * Fetches suggestions for the turn identified by requestId. Fire-and-forget from the caller's
   * perspective — never awaited on the answer-rendering path,
   * Self-supersedes: calling this again before a prior call resolves aborts the prior
   * call and clears its data, so a late response can never render under the wrong turn.
   */
  async fetchSuggestions(requestId: string, threadId: string): Promise<void> {
    this.abort();
    this.#abortController = new AbortController();
    const response: AskEvaSuggestionsResponse = await this.#askEvaClient.processSuggestions(
      requestId, threadId, this.#abortController.signal
    );
    if (response.requestId !== requestId) return;
    this.#suggestions.set(response.prompts ?? []);
  }
}
